<template>
  <b-form-group :label="title" label-class="label-style">
    <b-spinner
      v-if="loading"
      variant="primary"
      label="Chargement en cours..."
    />
    <template v-else>
      <multiselect
        :value="selectedValue"
        label="label"
        track-by="id"
        :options="options"
        :multiple="multiple"
        :show-labels="false"
        :hide-selected="false"
        :close-on-select="!multiple"
        :allow-empty="false"
        placeholder=""
        :group-values="groupValues"
        :group-label="groupLabel"
        :group-select="multiple"
        @input="updateSelectedValues"
      />
      <b-form-checkbox
        v-if="selectAll"
        v-model="selectAllOptions"
        name="select-all"
        switch
        @change="toggleSelectAll"
      >
        {{ $t("filter.select_all") }}
      </b-form-checkbox>
    </template>
  </b-form-group>
</template>

<script>
import Tools from "@/utils/tools";
import TableTools from "@/utils/tableTools";
import _ from "lodash";

export default {
  name: "FilterPickerWithId",
  props: {
    title: {
      type: String,
      default: "",
    },
    groupByKey: {
      type: String,
      default: "",
    },
    groupByLabel: {
      type: String,
      default: "",
    },
    itemKey: {
      type: String,
      default: "",
    },
    itemLabel: {
      type: String,
      default: "",
    },
    useDefaultValue: {
      type: Boolean,
      default() {
        return false;
      },
    },
    multiple: {
      type: Boolean,
      default() {
        return false;
      },
    },
    selectAll: {
      type: Boolean,
      default() {
        return false;
      },
    },
    filterBySite: {
      type: Boolean,
      default() {
        return false;
      },
    },
    operator: {
      type: String,
      default() {
        return "equals";
      },
      validator: function (value) {
        // The value must match one of these strings
        return [
          "equals",
          "notEquals",
          "contains",
          "notContains",
          "gt",
          "gte",
          "lt",
          "lte",
          "set",
          "notSet",
          "inDateRange",
          "notInDateRange",
          "beforeDate",
          "afterDate",
          "contains",
          "notContains",
          "gt",
          "gte",
        ].includes(value);
      },
    },
  },
  data: function () {
    return {
      options: [],
      selectedValue: null,
      selectedFilter: null,
      loading: true,
      groupLabel: "groupLabel",
      groupValues: "groupValues",
      selectAllOptions: false,
    };
  },
  computed: {
    optionsFlattened() {
      return this.options.map((o) => o[this.groupValues]).flat();
    },
  },
  mounted: async function () {
    this.$on("sites-updated", this.handleSitesUpdated);
    await this.loadData();
  },

  methods: {
    updateSelectedValues(selectedValues) {
      if (selectedValues) {
        if (!Array.isArray(selectedValues)) {
          selectedValues = [selectedValues];
        } else if (selectedValues.length < 1) {
          this.selectedValue = this.defaultOption(this.options);
          selectedValues = this.selectedValue;
        }
      } else {
        this.selectedValue = this.defaultOption(this.options);
        selectedValues = this.selectedValue;
      }
      this.selectedValue = selectedValues;
      const idValues = selectedValues.map((v) => v.id);

      this.selectAllOptions =
        this.selectedValue.length === this.optionsFlattened.length;
      if (selectedValues) {
        this.selectedFilter = {
          dimension: this.itemKey,
          operator: this.operator,
          values: idValues,
        };
      } else {
        this.selectedFilter = null;
      }
      // enregistre les préférences dans le store du navigateur
      Tools.StorageHelper.storeFilterSelectedValue(this.itemKey, idValues);
      this.$emit("filterWithIdChanged", {
        idFilter: this.selectedFilter,
        values: selectedValues,
      });
    },
    async loadData() {
      await this.loadOptions().catch(() => {
        if (!window.navigator.onLine) {
          Tools.Alerts.Error(
            this.$swal,
            this.$t("alerts.alertFilterPickerMessageNoConnexion"),
            this.$t("alerts.alertFilterPickerTitleNoConnexion")
          );
        } else {
          Tools.Alerts.Error(
            this.$swal,
            this.$t("alerts.alertFilterPickerMessage"),
            this.$t("alerts.alertFilterPickerTitle")
          );
        }
      });
      this.setDefaultSelectedValue(this.useDefaultValue);
      this.loading = false;
    },

    async handleSitesUpdated() {
      this.loading = true;
      await this.loadData();
    },
    loadOptions: async function () {
      var dataQuery = {
        dimensions: [
          this.groupByKey,
          this.groupByLabel,
          this.itemKey,
          this.itemLabel,
        ],
      };

      const sitesId = Tools.StorageHelper.getStoredFilterSelectedValue(
        "Site.id"
      );
      if (this.filterBySite && sitesId != null) {
        var filter = {
          dimension: "Site.id",
          operator: "equals",
          values: sitesId,
        };

        dataQuery.filters = [filter];
      }
      const resultSet = await TableTools.Helper.doRequest(dataQuery);
      this.options = _.chain(resultSet)
        .groupBy(this.groupByKey)
        .map((value) => {
          return {
            [this.groupLabel]: value[0][this.groupByLabel],
            [this.groupValues]: value
              .map((o) => {
                return {
                  id: o[this.itemKey],
                  label: o[this.itemLabel],
                };
              })
              .sort((a, b) =>
                a.label.localeCompare(b.label, undefined, {
                  sensitivity: "base",
                })
              ),
          };
        })
        .value();
    },
    toggleSelectAll() {
      if (this.selectAllOptions) {
        // if checked, gets all values into this.selectedValue
        if (this.options && this.options.length) {
          this.selectedValue = this.optionsFlattened;
        } else {
          this.selectedValue = [];
        }
      } else {
        // otherwise, removes all values except for one (the first)
        const selectedValues = this.selectedValue
          ? Array.isArray(this.selectedValue)
            ? this.selectedValue
            : [this.selectedValue]
          : [];
        this.selectedValue = [selectedValues[0]];
      }
      // register changes
      this.updateSelectedValues(this.selectedValue);
    },
    defaultOption: function (options) {
      return [options[0][this.groupValues][0]];
    },
    setDefaultSelectedValue: function (useDefaultValue) {
      //If there is no options, remove the selected value
      if (this.options === null || this.options.length === 0) {
        this.selectedValue = null;
      } else if (this.optionsFlattened && this.optionsFlattened.length) {
        const storedSelectedIdValues = Tools.StorageHelper.getStoredFilterSelectedValue(
          this.itemKey
        );
        if (storedSelectedIdValues && storedSelectedIdValues.length) {
          const selectableValues = this.optionsFlattened.filter((o) =>
            storedSelectedIdValues.some((id) => o.id === id)
          );
          this.selectedValue = this.multiple
            ? selectableValues
            : selectableValues[0];
          this.updateSelectedValues(this.selectedValue);
        }
        if (
          useDefaultValue &&
          (!this.selectedValue || !this.selectedValue.length)
        ) {
          this.selectedValue = this.defaultOption(this.options);
          this.updateSelectedValues(this.selectedValue);
        }
      }
    },
  },
};
</script>

<style scoped>
.label-style{
  flex: 1;
  text-align: left;
  font-weight: bold;
}
</style>